import { buildActivity, SOURCE_LABEL } from './activity.js';
import { categorize } from './miss_category.js';
import { failureReason, FAILURE_TEXT } from './reasons.js';
import { openedEntries, DAILY_GOAL } from './opened.js';
import { isParked, todoGroup, TODO_GROUPS } from './todo.js';
import { factKindsFor, FACT_LABEL } from './facts.js';
import { sfBlockerText, SF_FIELD_SHORT } from './sf_answers.js';
import { readyTrend } from './ready_trend.js';
import { AUTOMATION_USER } from './status.js';
import { neverWentOut } from './unsent.js';
// The Trends page (Cesar 2026-10-13 in the team DM, Rod "1a"): history and KPIs for the claims team, view-only. Pure: no I/O. Every number
// reuses the rule another screen already shows, so the page never disagrees with them:
//   claim numbers added per day   the 5:30 pm report's rule (opened.ts openedToday), one Pacific day at a time, as of now
//   activity by source            the Today page's "who started the call" (activity.ts buildActivity), one Pacific day at a time
//   needs you                     the Open issues rows a person must act on, by the to-do group the Matters page shows (todo.ts)
//   carrier feedback              why a dialed AI call failed, the 5:30 pm report's classifier (reasons.ts failureReason)
//   facts to collect              the fact each needs-you row asks a person to type first (facts.ts), plus State Farm's open requests
//   aged claim numbers            (Neila 2026-10-13) of each day's claim numbers added, how many came in 8-30 and more than 30 days after the
//                                 case opened (the case's Open Date, else when it was created; Pacific calendar days), plus today's aged
//                                 backlog: open auto cases with insurance but no claim number yet
//   ready to open vs opened       (Cesar 2026-10-20) the pool of unopened records that have a real insurer and a policy number, one Pacific
//                                 day at a time, against the claim numbers added (ready_trend.ts), and today's pool by why it is unopened
export const TREND_DAYS = 30;
const DAY = 86400000;
const ms = (s) => Date.parse(String(s ?? '').replace(/([+-]\d\d)(\d\d)$/, '$1:$2'));
const id15 = (s) => (s ?? '').slice(0, 15);
/** Cesar's five carrier-feedback buckets first, then the two the report also keeps. */
export const FEEDBACK_ORDER = ['missing', 'notFound', 'ai', 'elsewhere', 'noPerson', 'handedOff', 'other'];
export const FEEDBACK_LABEL = {
    missing: 'Missing information', notFound: 'Policy not found', ai: 'AI refusal', elsewhere: 'File somewhere else',
    noPerson: 'Dropped call or no person', handedOff: 'Handed to our staff', other: 'Other or no clear result',
};
export function dayLabel(day) {
    const [y, m, d] = day.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    const wd = dt.getUTCDay();
    return { label: `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][wd]} ${m}/${d}`, weekend: wd === 0 || wd === 6 };
}
const inDay = (iso, w) => { const t = ms(iso); return t >= ms(w.start) && t < ms(w.end); };
const median = (xs) => { if (!xs.length)
    return 0; const s = [...xs].sort((a, b) => a - b); const i = Math.floor(s.length / 2); return s.length % 2 ? s[i] : (s[i - 1] + s[i]) / 2; };
const days1 = (n) => Math.round(n * 10) / 10;
const pacificYmd = (t) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(t));
const ymdDays = (ymd) => { const [y, m, d] = ymd.slice(0, 10).split('-').map(Number); return Date.UTC(y, m - 1, d) / DAY; };
/** Case age in Pacific calendar days when the claim number came in, and which date it started from; null = the case dates are unknown. */
export function caseAgeAt(row, atMs) {
    const m = row.litify_pm__Matter__r;
    const open = m?.litify_pm__Open_Date__c && /^\d{4}-\d{2}-\d{2}/.test(m.litify_pm__Open_Date__c) ? m.litify_pm__Open_Date__c.slice(0, 10) : null;
    const created = m?.CreatedDate && Number.isFinite(ms(m.CreatedDate)) ? pacificYmd(ms(m.CreatedDate)) : null;
    const start = open ?? created;
    if (!start || !Number.isFinite(atMs))
        return null;
    return { days: ymdDays(pacificYmd(atMs)) - ymdDays(start), from: open ? 'open' : 'created' };
}
/** 0-7 days (exactly 7 included) / 8-30 (exactly 30 included) / more than 30. */
export function ageBucket(days) { return days <= 7 ? 'within7' : days <= 30 ? 'd8to30' : 'over30'; }
/** Claim numbers added, one Pacific day at a time: the report's rule on that day's history, as Salesforce stands now; and their case ages. */
export function addedByDay(input) {
    const o = input.opened;
    // a record created blank and filled on a later day is in createdRows (a number is there now) but is counted on its fill day: only a record
    // born filled counts on its creation day, and its first history entry tells which it was (Cesar 2026-10-22). The Matters tile and the email
    // read today alone, where this cannot occur.
    const first = new Map();
    for (const h of o?.history ?? []) {
        const at = Date.parse(String(h.CreatedDate).replace(/([+-]\d\d)(\d\d)$/, '$1:$2'));
        const f = first.get(h.ParentId);
        if (!f || at < f.at)
            first.set(h.ParentId, { at, blank: !String(h.OldValue ?? '').trim() });
    }
    const bornBlank = (id) => first.get(id)?.blank === true;
    return input.days.map((w) => {
        const none = { added: null, aged: null, from: { open: 0, created: 0 } };
        if (!o)
            return none;
        const r = openedEntries({
            history: o.history.filter((h) => inDay(h.CreatedDate, w)),
            openedRows: o.openedRows,
            createdRows: o.createdRows.filter((x) => inDay(x.CreatedDate, w) && !bornBlank(x.Id)),
            calls: o.calls, callHistory: o.callHistory, typed: o.typed,
            fromIso: new Date(ms(w.start) - 14 * DAY).toISOString(),
        });
        if (!r)
            return none;
        const a = { total: 0, ai: 0, handoff: (r.handoffKnown ? 0 : null), afterAi: 0, team: 0, bySystem: 0, plt: 0, def: 0 };
        const aged = { within7: 0, d8to30: 0, over30: 0, unknown: 0 };
        const from = { open: 0, created: 0 };
        for (const e of r.entries) {
            a.total++;
            a[e.side === 'PLT' ? 'plt' : 'def']++;
            if (e.how === 'system')
                a.bySystem++;
            else if (e.how === 'handoff')
                a.handoff = (a.handoff ?? 0) + 1;
            else
                a[e.how]++;
            const age = caseAgeAt(e.row, e.at);
            if (!age) {
                aged.unknown++;
                continue;
            }
            aged[ageBucket(age.days)]++;
            from[age.from]++;
        }
        return { added: a, aged, from };
    });
}
export { neverWentOut };
/** The Today page's totals, one Pacific day at a time, plus requests that never reached the claims caller (Cesar 2026-10-22, the 10/21 HTTP
 *  503s) and how many of those were not followed by another request on the same record. */
export function callsByDay(input) {
    const all = input.cans;
    if (!all)
        return input.days.map(() => null);
    return input.days.map((w) => {
        const cans = all.filter((c) => inDay(c.CreatedDate, w));
        if (!cans.length)
            return { requested: 0, placed: 0, claims: 0, hitRate: null, unsent: 0, unsentNotRetried: 0 };
        const a = buildActivity({ now: input.now, cans, users: {}, insurance: input.insurance, matters: {}, rearm: input.rearm, tokens: input.tokens,
            actions: input.actions ?? [], calling: [], dialed: input.dialed });
        // never went out = a MISSED request carrying the unsent reason (a live CAN can be Success and still carry that text)
        const unsent = cans.filter(neverWentOut);
        // retried = a LATER request on the same record (first 15 characters) that itself went out; a later never-went-out request is not a retry
        const notRetried = unsent.filter((u) => !u.Insurance__c || !all.some((c) => c.Id !== u.Id && id15(c.Insurance__c) === id15(u.Insurance__c)
            && ms(c.CreatedDate) > ms(u.CreatedDate) && !neverWentOut(c))).length;
        const { requests, placed, claims } = a.totals;
        return { requested: requests, placed, claims, hitRate: placed ? Math.round((100 * claims) / placed) : null, unsent: unsent.length, unsentNotRetried: notRetried };
    });
}
/** New PLT/DEF records per Pacific day: intake (the automation user) vs staff; a second client from the same crash is not counted. */
export function recordsByDay(days, rows) {
    if (!rows)
        return days.map(() => null);
    return days.map((w) => {
        const out = { intake: 0, staff: 0 };
        for (const r of rows) {
            if (!inDay(r.CreatedDate, w))
                continue;
            const m = r.litify_pm__Matter__r;
            if (m && m.Lead_Matter__c === false && String(m.litify_pm__Companion__c ?? '').trim())
                continue;
            out[r.CreatedById === AUTOMATION_USER ? 'intake' : 'staff']++;
        }
        return out;
    });
}
/** This week's Monday to yesterday against the same weekdays 7 days earlier (on a Monday: last Monday to Saturday against the week before).
 *  Today is never in it. null when any day in either span is unknown. */
export function weekCompare(days) {
    const last = days.length - 1;
    const end = days[last]?.partial ? last - 1 : last; // yesterday when today is still going
    if (end < 0)
        return null;
    let start = end;
    while (start > 0 && !days[start].label.startsWith('Mon'))
        start--;
    if (!days[start].label.startsWith('Mon'))
        return null;
    let to = end;
    if (days[last]?.partial && days[last].label.startsWith('Mon')) { // today is Monday: compare last full week
        to = last - 2; // Saturday (yesterday is Sunday)
        start = last - 7;
        if (start < 0 || !days[start].label.startsWith('Mon'))
            return null;
    }
    const span = days.slice(start, to + 1);
    const prior = days.slice(start - 7, to - 7 + 1);
    if (start - 7 < 0 || span.some((d) => !d.added) || prior.length !== span.length || prior.some((d) => !d.added))
        return null;
    return { thisWeek: sum(span.map((d) => d.added.ai)), lastWeek: sum(prior.map((d) => d.added.ai)), from: span[0].label, to: span[span.length - 1].label };
}
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
export function buildTrends(input) {
    const notes = [];
    const nowMs = input.now.getTime();
    const lastDay = input.days[input.days.length - 1];
    const per = addedByDay(input);
    const calls = callsByDay(input);
    const records = recordsByDay(input.days, input.newRecords);
    const days = input.days.map((w, i) => ({ day: w.day, ...dayLabel(w.day), partial: w === lastDay && ms(w.end) > nowMs,
        added: per[i]?.added ?? null, aged: per[i]?.aged ?? null, calls: calls[i] ?? null, records: records[i] ?? null }));
    const agedTotals = input.opened ? per.reduce((t, p) => {
        if (p.aged) {
            t.within7 += p.aged.within7;
            t.d8to30 += p.aged.d8to30;
            t.over30 += p.aged.over30;
            t.unknown += p.aged.unknown;
        }
        if (p.added)
            t.total += p.added.total;
        t.byOpenDate += p.from.open;
        t.byCreatedDate += p.from.created;
        return t;
    }, { within7: 0, d8to30: 0, over30: 0, unknown: 0, total: 0, byOpenDate: 0, byCreatedDate: 0 }) : null;
    if (!input.opened)
        notes.push('Claim numbers per day are not available right now (Salesforce did not answer).');
    // ---- activity by source: the Today page's rule, one day at a time
    let sources = null;
    let routerSplit = input.rearm !== null;
    if (input.cans) {
        const by = new Map();
        input.days.forEach((w, i) => {
            const cans = input.cans.filter((c) => inDay(c.CreatedDate, w));
            if (!cans.length)
                return;
            const a = buildActivity({ now: input.now, cans, users: {}, insurance: input.insurance, matters: {}, rearm: input.rearm, tokens: input.tokens,
                actions: input.actions ?? [], calling: [], dialed: input.dialed });
            routerSplit = a.routerSplit;
            for (const g of a.bySource) {
                if (!g.requests)
                    continue;
                const t = by.get(g.source) ?? { source: g.source, label: SOURCE_LABEL[g.source], requests: 0, placed: 0, claims: 0, daily: input.days.map(() => 0) };
                t.requests += g.requests;
                t.placed += g.placed;
                t.claims += g.claims;
                t.daily[i] = g.requests;
                by.set(g.source, t);
            }
        });
        sources = [...by.values()].sort((a, b) => b.requests - a.requests);
        if (!routerSplit)
            notes.push('Sweeper and Ins Creation calls show as one automation bucket: the router ledger could not be read.');
    }
    else
        notes.push('Activity by source and carrier feedback are not available right now (Salesforce did not answer).');
    // ---- carrier feedback: dialed calls that failed, by why (the 5:30 pm report's classifier)
    let feedback = null;
    if (input.cans) {
        const dialedSet = input.dialed ? new Set(input.dialed.map(id15)) : null;
        const from7 = lastDay ? ms(input.days[Math.max(0, input.days.length - 7)].start) : 0;
        const rows = new Map(FEEDBACK_ORDER.map((k) => [k, {
                key: k, label: FEEDBACK_LABEL[k], text: FAILURE_TEXT[k], last7: 0, last30: 0, carriers: [], daily: input.days.map(() => 0), by: new Map()
            }]));
        let m7 = 0, m30 = 0;
        for (const c of input.cans) {
            if (c.Disposition__c !== 'Miss')
                continue;
            if (categorize(c, { dialed: dialedSet ? dialedSet.has(id15(c.Id)) : null, nowMs }).group !== 'DIALED')
                continue; // never reached a carrier
            const i = input.days.findIndex((w) => inDay(c.CreatedDate, w));
            if (i < 0)
                continue;
            const r = rows.get(failureReason(c));
            r.last30++;
            m30++;
            r.daily[i] = (r.daily[i] ?? 0) + 1;
            if (ms(c.CreatedDate) >= from7) {
                r.last7++;
                m7++;
            }
            const name = (c.carrier ?? '').trim();
            if (name)
                r.by.set(name, (r.by.get(name) ?? 0) + 1);
        }
        feedback = { dialedMisses7: m7, dialedMisses30: m30, rows: FEEDBACK_ORDER.map((k) => {
                const { by, ...r } = rows.get(k);
                return { ...r, carriers: [...by.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 3).map(([name, n]) => ({ name, n })) };
            }) };
    }
    // ---- needs you + facts to collect: the Open issues rows a person must act on
    let needs = null;
    let facts = null;
    if (input.issues) {
        const live = input.issues.filter((i) => !isParked(i.row));
        const groups = new Map();
        const fresh = new Map();
        const kinds = new Map();
        for (const i of live) {
            const k = todoGroup(i.row);
            const waitMs = Math.max(0, nowMs - ms(i.row.waitingSince));
            const list = groups.get(k) ?? [];
            list.push(Number.isFinite(waitMs) ? waitMs / DAY : 0);
            groups.set(k, list);
            if (Number.isFinite(waitMs) && waitMs <= 7 * DAY)
                fresh.set(k, (fresh.get(k) ?? 0) + 1);
            const first = factKindsFor(i.row, { lorSend: input.lorSend === true })[0] ?? 'none';
            kinds.set(first, (kinds.get(first) ?? 0) + 1);
        }
        needs = [...groups.entries()].map(([key, w]) => ({ key, label: TODO_GROUPS[key].label, open: w.length, newIn7: fresh.get(key) ?? 0,
            oldestDays: days1(Math.max(...w)), medianDays: days1(median(w)) }))
            .sort((a, b) => b.open - a.open || b.oldestDays - a.oldestDays);
        const kindRows = [...kinds.entries()].map(([kind, open]) => ({ kind, open,
            label: kind === 'none' ? 'Nothing to type: a person calls, reads or confirms' : FACT_LABEL[kind] }))
            .sort((a, b) => (a.kind === 'none' ? 1 : 0) - (b.kind === 'none' ? 1 : 0) || b.open - a.open);
        facts = { kinds: kindRows, stateFarm: stateFarmOf(input.sfAnswers) };
    }
    else {
        notes.push('Needs-you tasks and facts to collect are not available right now (the open issues did not load).');
        facts = input.sfAnswers ? { kinds: [], stateFarm: stateFarmOf(input.sfAnswers) } : null;
    }
    // ---- ready to open vs opened: an AI call request in the window splits "AI tried" from "no AI call" (the calls unread: neither)
    // the section's numbers check themselves (ready_trend.ts assertReadyAddsUp): numbers that do not add up are never shown
    let ready = null;
    if (input.ready) {
        try {
            ready = readyTrend(input.days, input.ready, input.now, input.cans ? new Set(input.cans.flatMap((c) => (c.Insurance__c ? [id15(c.Insurance__c)] : []))) : null);
        }
        catch (e) {
            notes.push(`Ready to open per day is not shown: its numbers did not add up (${String(e.message).slice(0, 80)}).`);
        }
    }
    if (input.ready === null)
        notes.push('Ready to open per day is not available right now (Salesforce did not answer).');
    if (input.newRecords === undefined || input.newRecords === null)
        notes.push('New records per day are not available right now (Salesforce did not answer).');
    const today = days[days.length - 1];
    if (today?.partial)
        notes.push(`${today.label} is still going: its numbers grow until midnight.`);
    return { asOf: input.now.toISOString(), goal: DAILY_GOAL, days, week: weekCompare(days), aged: agedTotals, backlog: input.backlog ?? null, sources, routerSplit, needs, feedback, facts, ready, notes };
}
/** State Farm's open auto-file requests: which facts no record states, and what blocks the rest. */
export function stateFarmOf(a) {
    if (!a || a.source === 'none')
        return null;
    const open = a.rows.filter((r) => r.request && (r.request.state === 'needs_answers' || r.request.state === 'blocked'));
    const fields = new Map(), blockers = new Map();
    for (const r of open) {
        for (const f of r.request.fields ?? [])
            fields.set(f.key, (fields.get(f.key) ?? 0) + 1);
        // grouped by the blocker's reason code (2026-10-13, Kimi's change 7): the field key is null for most gate refusals, so grouping on it put
        // 90 of 96 under "Other reason" (65 of them "a claim number already exists") and showed the rest as raw field names
        for (const b of r.request.blockers ?? []) {
            const k = String(b.reason ?? '').trim() || 'OTHER';
            blockers.set(k, (blockers.get(k) ?? 0) + 1);
        }
    }
    const list = (m, label) => [...m.entries()].sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
        .map(([key, n]) => ({ key, label: label(key), n }));
    return {
        open: open.length, needsAnswers: open.filter((r) => r.request.state === 'needs_answers').length, blocked: open.filter((r) => r.request.state === 'blocked').length,
        fields: list(fields, (k) => SF_FIELD_SHORT[k] ?? k),
        blockers: list(blockers, (k) => sfBlockerText(k === 'OTHER' ? null : k)),
    };
}
//# sourceMappingURL=trends.js.map